import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import Header from '../components/Header';
import { api, SearchResult } from '../services/api';
import toast from 'react-hot-toast';

const SearchPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Perform search
  const performSearch = async (showRefreshToast = false) => {
    if (!query.trim()) {
      setSearchResults([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const results = await api.searchProducts(query, 10);
      setSearchResults(results);
      if (showRefreshToast) {
        toast.success('Search results refreshed');
      }
    } catch (error) {
      console.error('Search failed:', error);
      setError('Failed to load search results');
      setSearchResults([]);
      if (!showRefreshToast) {
        toast.error('Failed to load search results');
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Load search results
  useEffect(() => {
    performSearch();
  }, [query]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await performSearch(true);
  };

  return (
    <>
      <title>Search Results for "{query}" - ProductTrace</title>
      <meta name="description" content={`Search results for ${query} on ProductTrace`} />

      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Back Button and Refresh */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to ProductTrace
            </button>
            
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {isRefreshing ? (
                <>
                  <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                  Refreshing...
                </>
              ) : (
                <>
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  Refresh
                </>
              )}
            </button>
          </div>

          {/* Search Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Search Results for "{query}"
            </h1>
            <p className="text-gray-600">
              {isLoading && !isRefreshing ? 'Searching...' : `Found ${searchResults.length} results`}
            </p>
          </div>

          {/* Loading State */}
          {isLoading && !isRefreshing && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
              <span className="ml-3 text-gray-600">Searching...</span>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Search Failed
              </h3>
              <p className="text-gray-600 mb-6">
                {error}. Please try again.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Back to Home
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {isRefreshing ? (
                    <>
                      <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="w-4 h-4 mr-2" />
                      Try Again
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Search Results */}
          {!isLoading && !error && searchResults.length > 0 && (
            <div className="space-y-4">
              {searchResults.map((result, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/products/${result.slug}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <h3 className="text-xl font-semibold text-gray-900 mr-3">
                          {result.display_name}
                        </h3>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                          Product
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">
                        {result.brand ? `${result.brand} product` : 'Product analysis available'}
                      </p>
                      <div className="flex items-center text-sm text-gray-500">
                        <span className="mr-4">
                          📊 Analysis: {result.analysis_progress}% complete
                        </span>
                        <span className={`${result.analysis_status === 'completed' ? 'text-green-600' : 'text-yellow-600'}`}>
                          {result.analysis_status === 'completed' ? '✅ Complete' : '🔄 In Progress'}
                        </span>
                      </div>
                    </div>
                    <div className="text-blue-600 ml-4">
                      →
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Results */}
          {!isLoading && !error && searchResults.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No results found for "{query}"
              </h3>
              <p className="text-gray-600 mb-6">
                Try searching for a different product or check your spelling.
              </p>
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Back to Home
                </button>
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {isRefreshing ? (
                    <>
                      <div className="animate-spin w-4 h-4 mr-2 border-2 border-gray-400 border-t-transparent rounded-full" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="w-4 h-4 mr-2" />
                      Search Again
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SearchPage; 